import { Dispatch, SetStateAction, useCallback, useMemo, useState } from "react";
import { ListboxButton } from "@headlessui/react";
import { useTranslation } from "react-i18next";
import { IconType } from "react-icons";
import {
  HiOutlineArrowPath,
  HiOutlineClipboardDocumentList,
  HiOutlineFunnel,
  HiOutlineListBullet,
  HiOutlineMagnifyingGlass,
  HiOutlinePlus,
} from "react-icons/hi2";

import { useDeleteList } from "@/hooks/api/list/useDeleteList";
import { InfiniteListsParams, useInfiniteLists } from "@/hooks/api/list/useInfiniteLists";
import { useUpdateList } from "@/hooks/api/list/useUpdateList";
import { useTableConfig } from "@/hooks/table/useTableConfig";
import useDebounceQuery from "@/hooks/useDebounceQuery";
import Button from "@/components/atoms/Button";
import Input from "@/components/atoms/input/Input";
import Dropdown from "@/components/molecules/dropdown/Dropdown";
import CreateNewListModalHandler from "@/components/organisms/CreateNewListModalHandler";
import EnrichModalHandler from "@/components/organisms/EnrichModalHandler";
import { basicListColumns, basicListColumnsDef } from "@/components/organisms/list/basicListColumns";
import CreateFirstList from "@/components/organisms/list/CreateFirstList";
import NoListResults from "@/components/organisms/list/NoListResults";
import Table, { TableType } from "@/components/organisms/table/Table";

type Option = {
  id: "STATIC" | "DYNAMIC" | "ALL";
  name: string;
  icon: IconType;
};

const Lists = () => {
  const { t } = useTranslation();

  const { query, setDebounceQuery, isTyping } = useDebounceQuery();
  const [filter, setFilter] = useState<Option | null>(null);
  const listFilter: InfiniteListsParams = useMemo(
    () => ({
      query: query === "" ? null : query,
      types: filter === null || filter.id === "ALL" ? ["STATIC", "DYNAMIC"] : [filter?.id],
    }),
    [query, filter],
  );
  const { updateList } = useUpdateList({ listFilter });
  const { data, itemsTotal, isLoading, isFetched, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteLists(listFilter);
  const flattenedData = useMemo(() => data?.pages?.flatMap((page) => page.data) || [], [data]);

  const columnOrder = useMemo(() => Object.values(basicListColumns(t)).map((column) => column.id), [t]);
  const columnNames = useMemo(() => basicListColumns(t), [t]);
  const columns = useMemo(() => basicListColumnsDef(t), [t]);

  const { deleteList } = useDeleteList({ listFilter });

  const totalResults = useMemo(() => data?.pages[0].info?.items_total || 0, [data?.pages]);
  const loadingRowCount = useMemo(() => (isTyping ? Math.min(totalResults, 25) : 5), [isTyping, totalResults]);

  const onRename = useCallback(
    (id: string, newValue: string) => {
      updateList({ id, name: newValue });
    },
    [updateList],
  );

  const onDelete = useCallback(
    (ids: string[]) => {
      deleteList({ listIds: ids }, { onSuccess: () => tableConfig.clearRowSelection() });
    },
    [deleteList],
  );

  const columnValues = useMemo(() => Object.values(columnNames), [columnNames]);

  const tableConfig = useTableConfig({
    columns: columnValues,
    initialColumnOrder: columnOrder,
    onHiddenRowsChanged: onDelete,
  });

  return (
    <div className={"flex h-screen flex-grow flex-col overflow-y-auto overflow-x-hidden px-6 pb-4 pt-5"}>
      <div className={"mb-2 flex items-start justify-between"}>
        <h2 className={"flex items-center gap-2 text-2xl font-semibold"}>{t("static.lists")}</h2>
        <div className={"flex gap-3"}>
          <CreateNewListModalHandler
            variants={"secondary"}
            trailingIcon={<HiOutlinePlus className={"size-5"} />}
            label={t("static.create")}
          />
          <EnrichModalHandler />
        </div>
      </div>

      <div className={"mb-6 flex gap-3"}>
        <div className={"max-w-56"}>
          <Input
            leftPlaceholder={<HiOutlineMagnifyingGlass className={"size-5"} />}
            sizing={"xs"}
            onChange={(e) => setDebounceQuery(e.target.value)}
            placeholder={t("static.search")}
          />
        </div>

        <ListFilterDropdown filter={filter} setFilter={setFilter} itemsTotal={itemsTotal} />
      </div>

      {isFetched && flattenedData.length === 0 ? (
        query ? (
          <NoListResults query={query} />
        ) : (
          <CreateFirstList />
        )
      ) : (
        <Table
          allColumns={columns}
          data={flattenedData}
          tableType={TableType.LISTS}
          columnNames={columnNames}
          canReorderColumns={false}
          canReorderRows={false}
          isLoading={isLoading || isTyping}
          showHeaderWhileLoading
          loadMore={fetchNextPage}
          hasMoreData={hasNextPage}
          loadingRowCount={loadingRowCount}
          isFetchingNextPage={isFetchingNextPage}
          onRename={onRename}
          hasContextMenu={true}
          {...tableConfig}
          tableOptions={{
            enableHiding: false,
            columnResizeDirection: "ltr",
            columnResizeMode: "onChange",
            enableRowSelection: true,
            getRowId: (row) => row.id,
          }}
        />
      )}
    </div>
  );
};

export default Lists;

type Props = {
  itemsTotal: number;
  filter: Option | null;
  setFilter: Dispatch<SetStateAction<Option | null>>;
};
const ListFilterDropdown = ({ setFilter, filter, itemsTotal }: Props) => {
  const { t } = useTranslation();

  const options: Option[] = useMemo(
    () => [
      { id: "ALL", name: `${t("static.filter.allLists")} (${itemsTotal})`, icon: HiOutlineListBullet },
      { id: "STATIC", name: t("static.filter.staticLists"), icon: HiOutlineClipboardDocumentList },
      { id: "DYNAMIC", name: t("static.filter.dynamicLists"), icon: HiOutlineArrowPath },
    ],
    [itemsTotal],
  );

  return (
    <Dropdown
      options={options}
      selected={filter || options[0]}
      onSelect={setFilter}
      renderOption={(option) => (
        <button className={"flex items-center gap-2"}>
          <option.icon className={"size-5"} />
          <span className={"block truncate"}>{option.name}</span>
        </button>
      )}
      customButton={
        <>
          <ListboxButton className={"relative flex w-full items-center"}>
            <Button
              leadingIcon={<HiOutlineFunnel className={"size-5"} />}
              color={"natural"}
              variants={"outline"}
              size={"xs"}
              className={"!px-2"}
            />
          </ListboxButton>
          {filter !== null && filter.id !== "ALL" && (
            <div className={"absolute -right-0.5 top-0.5 size-2 rounded-full bg-brand-primary-solid"} />
          )}
        </>
      }
    />
  );
};
